import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaQuestionCircle,
  FaBook,
  FaUser,
  FaStar,
} from "react-icons/fa";

import {
  useGetQuizQuestionsQuery,
  useDeleteQuizQuestionMutation,
} from "../../slices/quizQuestionApiSlice";

import QuizQuestionImageBadge from "../../components/quiz/QuizQuestionImageBadge";

const QuestionListScreen = () => {
  const { quizId } = useParams();

  const navigate = useNavigate();

  const {
    data: quizQuestions = [],
    isLoading,
    error,
    refetch,
  } = useGetQuizQuestionsQuery(quizId);

  const questions = quizQuestions
    .filter((item) => item.question)
    .map((item) => ({
      ...item.question,
      quizQuestionId: item._id,
      order: item.order,
      quizMarks: item.marks,
    }));

  const [deleteQuestion, { isLoading: loadingDelete }] =
    useDeleteQuizQuestionMutation();

  const deleteHandler = async (quizQuestionId) => {
    if (
      window.confirm(
        "Are you sure you want to remove this question from the quiz?",
      )
    ) {
      try {
        await deleteQuestion(quizQuestionId).unwrap();

        toast.success("Question removed from quiz successfully");

        refetch();
      } catch (err) {
        toast.error(
          err?.data?.message ||
            err?.error ||
            "Failed to remove question from quiz",
        );
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-lg text-gray-600">
        Loading questions...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="rounded-xl bg-red-100 p-5 text-red-700 shadow-lg">
          {error?.data?.message || error.error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800">Quiz Questions</h1>

            <p className="mt-2 text-gray-500">
              Manage questions assigned to this quiz ({questions.length})
            </p>
          </div>

          <Link
            to={`/quizzes/${quizId}/add-questions`}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 font-semibold text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 transition"
          >
            <FaPlus />
            Add Questions
          </Link>
        </div>

        {loadingDelete && (
          <div className="mb-6 rounded-xl bg-blue-100 p-4 text-center text-blue-700">
            Removing question from quiz...
          </div>
        )}

        {questions.length === 0 ? (
          <div className="rounded-3xl bg-white p-12 text-center shadow-xl">
            <FaQuestionCircle className="mx-auto mb-4 text-6xl text-blue-600" />

            <h2 className="text-2xl font-bold text-gray-700">
              No Questions in this Quiz
            </h2>

            <p className="mt-3 text-gray-500">
              Add questions from the Question Bank to this quiz.
            </p>

            <Link
              to={`/quizzes/${quizId}/add-questions`}
              className="inline-flex items-center gap-2 mt-6 rounded-xl bg-blue-600 px-6 py-3 text-white font-semibold hover:bg-blue-700"
            >
              <FaPlus />
              Add Question
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {questions.map((question) => (
              <div
                key={question.quizQuestionId}
                className="rounded-3xl bg-white/90 backdrop-blur border border-gray-100 shadow-xl p-6 hover:shadow-2xl transition"
              >
                <div className="flex flex-col lg:flex-row gap-6">
                  <div className="flex-1">
                    <div className="flex justify-between gap-4 flex-wrap">
                      <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                        Q{question.order}. {question.questionText}
                      </h2>

                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="rounded-full bg-blue-100 px-4 py-1 text-blue-700 font-semibold">
                          {question.questionType}
                        </span>

                        <QuizQuestionImageBadge isImage={question.isImage} />
                      </div>
                    </div>

                    <div className="mt-5 grid md:grid-cols-2 gap-3">
                      {question.options?.map((option, index) => (
                        <div
                          key={option._id || index}
                          className={`rounded-xl border p-3 ${
                            option.key === question.correctAnswer
                              ? "bg-green-100 border-green-400 text-green-800"
                              : "bg-gray-50"
                          }`}
                        >
                          <span className="font-bold mr-2">{option.key}.</span>

                          {option.text}
                        </div>
                      ))}
                    </div>

                    {question.imageUrl && (
                      <div className="mt-5">
                        <p className="text-sm font-semibold text-gray-600 mb-2">
                          Question Image
                        </p>

                        <img
                          src={question.imageUrl}
                          alt="Question"
                          loading="lazy"
                          className="rounded-xl border max-h-72 max-w-full object-contain bg-gray-50 p-2"
                        />
                      </div>
                    )}

                    <div className="grid md:grid-cols-3 gap-4 mt-6">
                      <div>
                        <p className="text-xs uppercase text-gray-500">
                          Subject
                        </p>

                        <p className="flex items-center gap-2 font-semibold">
                          <FaBook className="text-blue-600" />

                          {question.subject?.name}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs uppercase text-gray-500">
                          Chapter
                        </p>

                        <p className="font-semibold">
                          {question.chapter?.name}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs uppercase text-gray-500">Marks</p>

                        <p className="flex items-center gap-2 font-semibold">
                          <FaStar className="text-yellow-500" />

                          {question.quizMarks ?? question.marks}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs uppercase text-gray-500">
                          Difficulty
                        </p>

                        <p className="font-semibold capitalize">
                          {question.difficulty}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs uppercase text-gray-500">
                          Correct Answer
                        </p>

                        <p className="font-semibold text-green-700">
                          {
                            question.options?.find(
                              (option) => option.key === question.correctAnswer,
                            )?.text
                          }{" "}
                          ({question.correctAnswer})
                        </p>
                      </div>

                      <div>
                        <p className="text-xs uppercase text-gray-500">
                          Created By
                        </p>

                        <p className="flex items-center gap-2 font-semibold">
                          <FaUser className="text-indigo-600" />

                          {question.createdBy?.name}
                        </p>
                      </div>
                    </div>

                    {question.explanation && (
                      <div className="mt-5 rounded-xl bg-slate-50 border p-4">
                        <p className="text-sm font-semibold text-gray-600">
                          Explanation
                        </p>

                        <p className="mt-2 text-gray-700">
                          {question.explanation}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}

                  <div className="flex lg:flex-col gap-3 lg:w-44">
                    <button
                      onClick={() => navigate(`/questions/${question._id}`)}
                      className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-white hover:bg-blue-700 transition"
                    >
                      <FaEye />
                      View
                    </button>

                    <button
                      onClick={() =>
                        navigate(`/questions/${question._id}/edit`)
                      }
                      className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-3 text-white hover:bg-amber-600 transition"
                    >
                      <FaEdit />
                      Edit
                    </button>

                    {question.isImage && (
                      <button
                        onClick={() =>
                          navigate(
                            `/question-image-management/${quizId}?question=${question._id}`,
                          )
                        }
                        className={`flex-1 flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-white transition ${
                          question.imageUrl
                            ? "bg-green-600 hover:bg-green-700"
                            : "bg-purple-600 hover:bg-purple-700"
                        }`}
                      >
                        🖼 {question.imageUrl ? "Change Image" : "Upload Image"}
                      </button>
                    )}

                    <button
                      onClick={() => deleteHandler(question.quizQuestionId)}
                      className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-3 text-white hover:bg-red-700 transition"
                    >
                      <FaTrash />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionListScreen;
